import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Mail, Lock, Eye, EyeOff, User, Phone, LogIn, UserPlus, 
  CheckCircle, Clock, AlertCircle 
} from "lucide-react";
import api from "../lib/api";

export default function Login() {
  const [activeTab, setActiveTab] = useState("login");
  
  // Login state
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Register state
  const [registerForm, setRegisterForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: ""
  });
  const [registering, setRegistering] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("¡Bienvenido!");
      navigate("/dashboard");
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (!error.response || status >= 500 || status === 0) {
        toast.error("No se pudo conectar con el servidor. Intenta de nuevo en unos segundos.", {
          duration: 6000
        });
      } else if (detail?.includes("pendiente")) {
        toast.error("Tu cuenta está pendiente de aprobación por el administrador.", {
          duration: 5000
        });
      } else if (detail?.includes("rechazada")) {
        toast.error("Tu solicitud fue rechazada. Contacta al administrador.");
      } else {
        toast.error(detail || "Credenciales inválidas");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    
    if (registerForm.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    
    setRegistering(true);
    try {
      await api.post("/auth/register", {
        name: registerForm.name,
        email: registerForm.email,
        password: registerForm.password,
        phone: registerForm.phone,
        role: "vendedor" // Default role request
      });
      
      setRegisterSuccess(true);
      toast.success("Registro exitoso", { duration: 5000 });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrarse");
    } finally {
      setRegistering(false);
    }
  };

  // Success screen after registration
  if (registerSuccess) {
    return (
      <div className="min-h-screen flex" data-testid="login-page">
        {/* Left side - Image */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
          <img
            src="/cover-tenatour.jpg"
            alt="TenaTour Transport"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/60" />
          <div className="relative z-10 p-12 flex flex-col justify-between h-full">
            <div className="flex items-center">
              <img 
                src="/logo-tenatour.png" 
                alt="TenaTour" 
                className="h-20 w-auto object-contain brightness-0 invert"
              />
            </div>
            <div>
              <h1 className="text-4xl font-heading font-bold text-white mb-4">
                Transportes TenaTour
              </h1>
              <p className="text-lg text-slate-300 max-w-md">
                Sistema integral de gestión de transporte corporativo.
              </p>
            </div>
          </div>
        </div>

        {/* Right side - Success message */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
          <div className="w-full max-w-md">
            <div className="lg:hidden flex items-center justify-center mb-8">
              <img 
                src="/logo-tenatour.png" 
                alt="TenaTour" 
                className="h-16 w-auto object-contain"
              />
            </div>

            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Clock className="w-8 h-8 text-amber-600" />
                </div>
                
                <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
                  ¡Registro Exitoso!
                </h2>
                
                <div className="p-4 bg-amber-50 rounded-lg mb-6">
                  <p className="text-amber-800 font-medium mb-2">
                    Cuenta pendiente de validar
                  </p>
                  <p className="text-sm text-amber-700">
                    Tu solicitud ha sido enviada al administrador. 
                    Recibirás acceso una vez que tu cuenta sea aprobada y se te asigne un rol.
                  </p>
                </div>
                
                <div className="space-y-3 text-left text-sm text-slate-600 mb-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
                    <span>Tu información ha sido registrada correctamente</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-amber-500 mt-0.5" />
                    <span>El administrador revisará tu solicitud</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
                    <span>Se te notificará cuando tu cuenta esté activa</span>
                  </div>
                </div>
                
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setRegisterSuccess(false);
                    setActiveTab("login");
                    setRegisterForm({
                      name: "",
                      email: "",
                      password: "",
                      confirmPassword: "",
                      phone: ""
                    });
                  }}
                >
                  Volver al Inicio de Sesión
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="/cover-tenatour.jpg"
          alt="TenaTour Transport"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="relative z-10 p-12 flex flex-col justify-between h-full">
          <div className="flex items-center">
            <img 
              src="/logo-tenatour.png" 
              alt="TenaTour" 
              className="h-20 w-auto object-contain brightness-0 invert"
            />
          </div>
          <div>
            <h1 className="text-4xl font-heading font-bold text-white mb-4">
              Transportes TenaTour
            </h1>
            <p className="text-lg text-slate-300 max-w-md">
              Sistema integral de gestión de transporte corporativo. Control total de operaciones, flotas y finanzas.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Forms */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img 
              src="/logo-tenatour.png" 
              alt="TenaTour" 
              className="h-16 w-auto object-contain"
            />
          </div>

          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="space-y-1 pb-4">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="login" className="gap-2" data-testid="login-tab">
                    <LogIn className="w-4 h-4" />
                    Ingresar
                  </TabsTrigger>
                  <TabsTrigger value="register" className="gap-2" data-testid="register-tab">
                    <UserPlus className="w-4 h-4" />
                    Registrarse
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </CardHeader>
            
            <CardContent>
              {/* Login Form */}
              {activeTab === "login" && (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Correo Electrónico</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="correo@ejemplo.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10"
                        required
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Contraseña</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10"
                        required
                        data-testid="password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 font-medium"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Ingresando...
                      </span>
                    ) : (
                      "Ingresar"
                    )}
                  </Button>
                </form>
              )}

              {/* Register Form */}
              {activeTab === "register" && (
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="reg-name">Nombre Completo *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="reg-name"
                        type="text"
                        placeholder="Tu nombre completo"
                        value={registerForm.name}
                        onChange={(e) => setRegisterForm(prev => ({ ...prev, name: e.target.value }))}
                        className="pl-10"
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-email">Correo Electrónico *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="reg-email"
                        type="email"
                        placeholder="correo@ejemplo.com"
                        value={registerForm.email}
                        onChange={(e) => setRegisterForm(prev => ({ ...prev, email: e.target.value }))}
                        className="pl-10"
                        required
                        data-testid="register-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-phone">Teléfono</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="reg-phone"
                        type="tel"
                        placeholder="10 dígitos"
                        value={registerForm.phone}
                        onChange={(e) => setRegisterForm(prev => ({ ...prev, phone: e.target.value }))}
                        className="pl-10"
                        data-testid="register-phone-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-password">Contraseña *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="reg-password"
                        type="password"
                        placeholder="Mínimo 6 caracteres"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm(prev => ({ ...prev, password: e.target.value }))}
                        className="pl-10"
                        required
                        minLength={6}
                        data-testid="register-password-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reg-confirm">Confirmar Contraseña *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="reg-confirm"
                        type="password"
                        placeholder="Repite tu contraseña"
                        value={registerForm.confirmPassword}
                        onChange={(e) => setRegisterForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className="pl-10"
                        required
                        data-testid="register-confirm-input"
                      />
                    </div>
                  </div>

                  <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                    <p className="flex items-start gap-2">
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        Al registrarte, tu cuenta quedará pendiente de aprobación por el administrador.
                      </span>
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-11 font-medium"
                    disabled={registering}
                    data-testid="register-submit-btn"
                  >
                    {registering ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Registrando...
                      </span>
                    ) : (
                      "Crear Cuenta"
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
